import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import apiClient from '../../api/client'
import { errorMessage } from '../../api/ApiError'
import { descargarDocumento, descargarVarios } from '../../descargas'
import { separarRechazados } from '../../documentos'
import { agruparPorRequisito, type EstadoDelRequisito } from '../../documentosDelCaso'
import {
  canalLegible, fechaLima, horaLima, lineaDeCierre, quienDescargo, rutaDelChat, usuarioDe,
  type ComoCerro, type Descarga, type DocumentoDelReporte, type FilaExpediente,
  type TonoDeLinea,
} from '../../expedientes'
import { telefonoVisible } from '../../identidad'
import { ChipCompletoPorIA } from '../inbox/ConversationList'
import SelectorAsesor from '../inbox/SelectorAsesor'
import type { Advisor, ConversationDetail, Message } from '../../types/portal'

/**
 * El panel del expediente: Documentos y Chat (03-nov-2026).
 *
 * Pedido de Leslie y de Ruben: «se ve muy cargado» y «¿por qué dos botones por
 * fila?». La fila no lleva botones —tocarla abre esto— y el panel es EL MISMO
 * para el asesor, el supervisor y el admin: por rol cambian solo «Reasignar»,
 * que es de supervisión, y a dónde abre el chat completo (`rutaDelChat`).
 *
 * Lee el detalle de la conversación (`?vista=sesion`, lo mismo que el hilo de
 * la bandeja) y agrupa los documentos por requisito con `agruparPorRequisito`,
 * el mismo cálculo de la Ficha: lo que dice «validado» acá es lo que dice allá.
 */

export type ModoDelPanel = 'asesor' | 'supervision'

/** Lo último del chat, para saber en qué quedó sin abrirlo. */
const MENSAJES_EN_EL_PANEL = 4

const TONO_DE_LINEA: Record<TonoDeLinea, string> = {
  brand: 'text-brand-hover',
  advisor: 'text-advisor',
  warn: 'text-warn',
  danger: 'text-danger',
  muted: 'text-ink-muted',
}

/** La línea de cómo cerró, con su tono. Nada si no está listo. */
export function LineaDeCierre({ fila }: { fila: FilaExpediente }) {
  const linea = lineaDeCierre(fila)
  if (!linea) return null
  return <span className={`text-micro font-bold ${TONO_DE_LINEA[linea.tono]}`}>{linea.texto}</span>
}

/** El chip de cómo cerró. «Completo por IA» es el de Mi bandeja. */
export function ChipDeCierre({ como }: { como: ComoCerro }) {
  if (como === 'ia') return <ChipCompletoPorIA />
  if (como === 'asesor') return <span className="badge-blue">Completo con asesor</span>
  if (como === 'alertas') return <span className="badge-amber">Con alertas</span>
  if (como === 'critica') return <span className="badge-red">Alerta crítica</span>
  return null
}

const BARRA: Record<DocumentoDelReporte['estado'], string> = {
  recibido: 'bg-brand',
  observado: 'bg-warn-dot',
  pendiente: 'bg-surface-strong',
}

/**
 * «5/6» y una barra por documento: verde recibido, ámbar observado, gris
 * falta. Reemplaza a los chips con el nombre de cada uno, que eran lo que más
 * cargaba la fila; el nombre va en el tooltip y en el panel.
 */
export function BarrasDeDocumentos({ fila }: { fila: FilaExpediente }) {
  if (!fila.requeridos) return <span className="text-ink-faint">—</span>
  const observados = fila.documentos.filter((d) => d.estado === 'observado').length
  const resumen = `${fila.recibidos} de ${fila.requeridos} recibidos`
    + (observados ? `, ${observados} observado${observados === 1 ? '' : 's'}` : '')
  return (
    <span className="inline-flex items-center gap-2" role="img" aria-label={resumen}
          title={fila.documentos.map((d) => `${d.label}: ${d.estado}`).join('\n')}>
      <span className="min-w-[28px] font-mono text-meta text-ink">{fila.recibidos}/{fila.requeridos}</span>
      <span className="flex gap-[3px]" aria-hidden>
        {fila.documentos.map((d, i) => (
          <span key={`${d.corto}-${i}`} className={`h-[6px] w-[14px] rounded-full ${BARRA[d.estado]}`} />
        ))}
      </span>
    </span>
  )
}

const ESTADO_DEL_DOCUMENTO: Partial<Record<EstadoDelRequisito, { texto: string; clase: string }>> = {
  validado: { texto: 'Validado', clase: 'text-brand-hover' },
  observado: { texto: 'Observado', clase: 'text-warn' },
  rechazado: { texto: 'Rechazado', clase: 'text-danger' },
  pedido: { texto: 'Pedido de nuevo', clase: 'text-danger' },
  sin_recibir: { texto: 'Falta', clase: 'text-ink-muted' },
}

const QUIEN_ESCRIBIO: Partial<Record<Message['sender'], string>> = {
  user: 'Cliente', agent: 'Agente IA', operator: 'Asesor',
}

export default function PanelExpediente({ fila, modo, onCerrar, onDescargado }: {
  fila: FilaExpediente
  modo: ModoDelPanel
  onCerrar: () => void
  /** Se marcó la descarga: la lista se refresca y la fila cambia de paso. */
  onDescargado: (descarga: Descarga) => void
}) {
  const id = fila.conversation_id
  const [pestana, setPestana] = useState<'documentos' | 'chat'>('documentos')
  const [detail, setDetail] = useState<ConversationDetail | null>(null)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [bajando, setBajando] = useState(false)
  const [aviso, setAviso] = useState<string | null>(null)
  const [advisors, setAdvisors] = useState<Advisor[] | null>(null)
  const [reasignando, setReasignando] = useState(false)

  useEffect(() => {
    let vigente = true
    setDetail(null)
    setCargando(true)
    setError(null)
    setAviso(null)
    setReasignando(false)
    apiClient.get<ConversationDetail>(`/conversations/${encodeURIComponent(id)}`,
                                      { params: { vista: 'sesion' } })
      .then((res) => { if (vigente) setDetail(res.data) })
      .catch((err) => { if (vigente) setError(errorMessage(err, 'No se pudo abrir el expediente.')) })
      .finally(() => { if (vigente) setCargando(false) })
    return () => { vigente = false }
  }, [id])

  const caso = detail
    ? agruparPorRequisito(separarRechazados(detail.documents).tarjetas,
                          detail.progreso?.requeridos, detail.checklist_generated_at ?? '')
    : null
  const carpeta = detail?.documents.find((d) => d.drive_folder_url)?.drive_folder_url ?? ''

  const marcar = useCallback(async (origen: 'portal' | 'manual') => {
    const res = await apiClient.post<{ descarga: Descarga }>(
      `/expedientes/${encodeURIComponent(id)}/descarga`, { origen })
    onDescargado(res.data.descarga)
  }, [id, onDescargado])

  // «Descargar todo» baja lo validado de cada requisito, uno tras otro, y
  // recién si bajó todo queda «Descargado por ti». Un archivo que falló no
  // marca nada: el expediente sigue por descargar.
  const descargarTodo = async () => {
    if (!caso) return
    setBajando(true)
    setAviso(null)
    try {
      const archivos = caso.descargables.map((d) => ({ docId: d.doc.doc_id, nombre: d.nombre }))
      const fallidos = await descargarVarios(archivos)
      if (fallidos.length) {
        setAviso(`No se pudo bajar ${fallidos.length} de ${archivos.length}. Vuelve a intentarlo.`)
        return
      }
      if (fila.completo_ts) await marcar('portal')
    } catch (err) {
      setAviso(errorMessage(err, 'No se pudo registrar la descarga.'))
    } finally {
      setBajando(false)
    }
  }

  const yaLoBaje = async () => {
    setAviso(null)
    try {
      await marcar('manual')
    } catch (err) {
      setAviso(errorMessage(err, 'No se pudo registrar la descarga.'))
    }
  }

  const abrirReasignar = async () => {
    setReasignando(true)
    if (advisors) return
    try {
      const res = await apiClient.get<{ advisors: Advisor[] }>('/advisors')
      setAdvisors(res.data.advisors ?? [])
    } catch (err) {
      setAviso(errorMessage(err, 'No se pudo cargar la lista de asesores.'))
      setReasignando(false)
    }
  }

  const reasignarA = async (email: string) => {
    try {
      await apiClient.post(`/conversations/${encodeURIComponent(id)}/assign`, { target_email: email })
      setAviso(`Reasignado a ${usuarioDe(email)}.`)
      setReasignando(false)
    } catch (err) {
      setAviso(errorMessage(err, 'No se pudo reasignar.'))
    }
  }

  const ultimos = (detail?.messages ?? [])
    .filter((m) => QUIEN_ESCRIBIO[m.sender])
    .slice(-MENSAJES_EN_EL_PANEL)
  const paso = fila.descarga ? `Descargado · ${quienDescargo(fila.descarga)}`
    : fila.completo_ts ? 'Por descargar' : fila.fase_label

  return (
    <aside aria-label={`Expediente de ${fila.nombre || 'cliente sin identificar'}`}
           className="card flex min-h-0 flex-col overflow-hidden">
      <div className="flex flex-col gap-2.5 px-[18px] pt-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 className="truncate text-[16px] font-extrabold text-ink">{fila.nombre || 'Sin identificar'}</h2>
            <p className="text-micro text-ink-muted">
              {[fila.dni && `DNI ${fila.dni}`, telefonoVisible(fila), canalLegible(fila.canal)]
                .filter(Boolean).join(' · ')}
            </p>
          </div>
          <button type="button" className="btn-ghost !px-2" onClick={onCerrar} aria-label="Cerrar el expediente">
            <svg aria-hidden width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2.4" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ChipDeCierre como={fila.como_cerro} />
          <span className="text-micro font-bold text-ink-soft">{paso}</span>
        </div>
        <dl className="grid grid-cols-2 gap-x-3.5 gap-y-1.5 text-body">
          <div>
            <dt className="text-[11px] font-bold text-ink-faint">Asesor</dt>
            <dd className="font-bold text-ink">
              {modo === 'asesor' ? 'Tú' : (usuarioDe(fila.asesor) || 'Sin asignar')}
              {modo === 'supervision' && (
                <> · <button type="button" className="font-bold text-info-ink underline"
                             onClick={() => void abrirReasignar()}>Reasignar</button></>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] font-bold text-ink-faint">{fila.completo_ts ? 'Listo' : 'Entrada'}</dt>
            <dd className="font-bold text-ink">{fechaLima(fila.completo_ts || fila.entrada)}</dd>
          </div>
        </dl>
        <div role="tablist" aria-label="Expediente" className="flex gap-1 border-b border-surface-border">
          {(['documentos', 'chat'] as const).map((p) => (
            <button key={p} type="button" role="tab" aria-selected={pestana === p}
                    onClick={() => setPestana(p)}
                    className={`-mb-px border-b-2 px-3 py-2 text-body ${pestana === p
                      ? 'border-brand font-extrabold text-ink' : 'border-transparent font-semibold text-ink-muted'}`}>
              {p === 'documentos' ? `Documentos${fila.requeridos ? ` ${fila.requeridos}` : ''}` : 'Chat'}
            </button>
          ))}
        </div>
      </div>

      <div role="tabpanel" className="scrollbar-thin min-h-0 flex-1 overflow-y-auto px-[18px] py-2">
        {cargando ? (
          <p className="py-6 text-center text-body text-ink-muted">Abriendo el expediente…</p>
        ) : error ? (
          <p className="py-6 text-center text-body text-danger">{error}</p>
        ) : pestana === 'documentos' ? (
          <ul className="divide-y divide-surface-faint">
            {(caso?.grupos ?? []).filter((g) => g.estado !== 'no_aplica').map((g) => {
              const estado = ESTADO_DEL_DOCUMENTO[g.estado]
              const archivo = g.archivo
              return (
                <li key={g.clave} className="flex min-h-[44px] items-center gap-2">
                  <span className="min-w-0 flex-1 text-body font-semibold text-ink">{g.label}</span>
                  {estado && (
                    <span className={`text-micro font-bold ${estado.clase}`}>
                      {g.estado === 'validado'
                        ? `Validado por ${g.validadoPor === 'persona' ? 'una persona' : 'IA'}`
                        : estado.texto}
                    </span>
                  )}
                  {g.estado === 'validado' && archivo && (
                    <button type="button" className="btn-ghost !px-2" aria-label={`Descargar ${g.label}`}
                            onClick={() => void descargarDocumento(archivo.doc_id, g.label)
                              .catch((err) => setAviso(errorMessage(err, 'No se pudo bajar el archivo.')))}>
                      <svg aria-hidden width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                           strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 4v11M7 10l5 5 5-5M5 20h14" />
                      </svg>
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
        ) : (
          <div className="flex flex-col gap-2 py-1">
            <p className="text-[11px] font-extrabold uppercase tracking-wide text-ink-faint">
              Últimos mensajes · solo lectura
            </p>
            {ultimos.length === 0 && <p className="text-body text-ink-muted">Todavía no hay mensajes.</p>}
            {ultimos.map((m) => {
              const cliente = m.sender === 'user'
              return (
                <div key={m.message_id}
                     className={`max-w-[85%] rounded-lg border px-3 py-2 ${cliente
                       ? 'self-start border-surface-border bg-white'
                       : m.sender === 'operator' ? 'self-end border-info-border bg-info-light'
                         : 'self-end border-brand-border bg-brand-light'}`}>
                  <p className="text-[11px] font-bold text-ink-muted">
                    {QUIEN_ESCRIBIO[m.sender]} · {horaLima(m.timestamp)}
                  </p>
                  <p className="line-clamp-3 text-body text-ink">{m.text || 'Archivo'}</p>
                </div>
              )
            })}
            <Link to={rutaDelChat(id, modo)} className="btn-primary mt-2 justify-center">
              Abrir chat completo
            </Link>
          </div>
        )}
      </div>

      {aviso && <p className="px-[18px] pb-1 text-micro font-semibold text-ink-soft" role="status">{aviso}</p>}

      {reasignando && advisors && (
        <SelectorAsesor
          advisors={advisors}
          actual={fila.asesor || null}
          busy={false}
          onElegir={(email) => void reasignarA(email)}
          onCancelar={() => setReasignando(false)}
        />
      )}

      {pestana === 'documentos' && !cargando && !error && (
        <div className="flex flex-col gap-1.5 border-t border-surface-faint px-[18px] py-3.5">
          <button type="button" className="btn-primary justify-center"
                  disabled={bajando || !caso || caso.descargables.length === 0}
                  onClick={() => void descargarTodo()}>
            {bajando ? 'Bajando…' : `↓ Descargar todo (${caso?.descargables.length ?? 0})`}
          </button>
          <p className="text-center text-micro text-ink-muted">
            {carpeta && (
              <a href={carpeta} target="_blank" rel="noreferrer" className="font-bold text-brand-hover">
                Abrir carpeta en Drive
              </a>
            )}
            {carpeta && fila.completo_ts && !fila.descarga && ' · '}
            {fila.completo_ts && !fila.descarga && (
              <button type="button" className="font-bold text-info-ink underline" onClick={() => void yaLoBaje()}>
                Ya lo bajé de Drive
              </button>
            )}
          </p>
        </div>
      )}
    </aside>
  )
}
